/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 PRO React layouts
import Analytics from "layouts/dashboards/analytics";
import PrevProj from "layouts/dashboards/prevProj";
import NewUser from "layouts/administration/newUser";
import Settings from "layouts/users/settings";
import ManageUsers from "layouts/administration/manageUsers";
import Authentication from "layouts/authentication/sign-in";
import Importation from "layouts/administration/importation";
import CustomLink from "layouts/administration/customLink";

import { AuthUser } from "contexts/contextUser";

// @mui icons
import Icon from "@mui/material/Icon";

const routes = (
  userData: AuthUser,
  signOut: () => Promise<void>,
  customLink?: { label: string; href: string }
) => [
  userData?.user_email
    ? {
        type: "collapse",
        name: userData?.user_email?.slice(0, 15) + "...",
        key: "users",
        icon: <Icon fontSize="medium">person</Icon>,
        collapse: [
          {
            name: "Settings",
            key: "profile-settings",
            route: "/users/settings",
            component: <Settings />,
          },
          {
            name: "Logout",
            key: "logout",
            onClick: signOut,
          },
        ],
      }
    : {
        type: "collapse",
        name: "Sign In/Sign Up",
        key: "authentication",
        icon: <Icon fontSize="medium">content_paste</Icon>,
        noCollapse: true,
        route: "/authentication",
        component: <Authentication />,
      },
  { type: "divider", key: "divider-0" },
  { type: "title", title: "Pages", key: "title-pages" },
  {
    type: "collapse",
    name: "Administration",
    key: "administration",
    icon: <Icon fontSize="medium">supervisor_account</Icon>,
    adminOnly: true,
    collapse: [
      {
        name: "Manage Users",
        key: "manage-users",
        route: "/administration/manage-users",
        component: <ManageUsers />,
        adminOnly: true,
      },
      {
        name: "Importation",
        key: "importation",
        route: "/administration/importation",
        component: <Importation />,
        adminOnly: true,
      },
      {
        name: "Custom Link",
        key: "custom-link",
        route: "/administration/custom-link",
        component: <CustomLink />,
        adminOnly: true,
      },
      {
        name: "New User",
        key: "new-user",
        route: "/administration/new-user",
        component: <NewUser />,
        hidden: true,
        adminOnly: true,
      },
    ],
  },
  {
    type: "collapse",
    name: "Dashboards",
    key: "dashboards",
    icon: <Icon fontSize="medium">dashboard</Icon>,
    collapse: [
      {
        name: "Analytics",
        key: "analytics",
        route: "/dashboards/analytics",
        component: <Analytics />,
      },
      {
        name: "Prevalence Breakdown",
        key: "prevalence-breakdown",
        route: "/dashboards/prevalence-breakdown",
        component: <PrevProj />,
      },
    ],
  },
  {
    type: "collapse",
    name: customLink?.label ?? "External Link",
    key: "external-link",
    icon: <Icon fontSize="medium">link</Icon>,
    noCollapse: true,
    hidden: !(customLink?.label && customLink?.href),
    href: customLink?.href,
  },
];

export default routes;
